import { useEffect, useState } from "react";
import { useUserRole, useUserCreateRoleOptions } from "../../utils/hooks/useRoles";
import { useIndependentUsers } from "../../utils/hooks/useUsers";
import SelectFields from "../common/SelectFields";
import PLATFORM_TYPES_ENUM from "../../lib/enums/platformTypes";
import { useTranslation } from "react-i18next";

const NO_PARENT_ROLE_NAMES = ["Admin", "Main Client", "Contractor Admin"];

const UserAssignRoleSelector = ({
  parentUser,
  selectedRoleId,
  onRoleChange,
  onParentUserChange,
}) => {
  const { t } = useTranslation();

  const [ownerSearchQuery, setOwnerSearchQuery] = useState("");

  const { data: userRole, isLoading: userRoleLoading } = useUserRole();

  const isUserAdmin = userRole?.platform_type === PLATFORM_TYPES_ENUM.ADMIN;

  const { data: roleOptions, isLoading: roleOptionsLoading } = useUserCreateRoleOptions(parentUser);
  const { data: owners, isLoading: ownersLoading } = useIndependentUsers({
    searchQuery: ownerSearchQuery,
  });

  useEffect(() => {
    onRoleChange(null);
  }, [parentUser]);

  const selectedRole = roleOptions?.find((role) => role.id === selectedRoleId);
  const roleTakesNoParent =
    !!selectedRole && selectedRole.is_system && NO_PARENT_ROLE_NAMES.includes(selectedRole.name);

  return (
    <>
      <SelectFields
        name="role_id"
        disabled={roleOptionsLoading}
        label={t("userManagement.data.role")}
        required={true}
        placeholder={t("userManagement.data.role")}
        options={roleOptions?.map((role) => ({
          value: role.id,
          label: role.name,
          description: role.description,
        }))}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return (
            <li
              key={key + option.value}
              {...optionProps}
              className="py-2 px-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
            >
              <p className="text-base font-semibold">{option.label}</p>
              <p className="text-sm text-gray-600">{option.description}</p>
            </li>
          );
        }}
        onChange={(e) => {
          const newId = e.target.value;
          const newRole = roleOptions?.find((opt) => opt.id === newId);
          onRoleChange(newRole);
        }}
      />
      {isUserAdmin && (
        <SelectFields
          name="parent_user_id"
          disabled={ownersLoading || roleTakesNoParent}
          label={t("userManagement.data.parentUser")}
          placeholder={t("userManagement.data.parentUser")}
          options={owners?.data?.map((owner) => ({
            value: owner.id,
            label: `${owner.first_name} ${owner.last_name}`,
            companyName: owner.company_name,
            role: owner.role?.name,
          }))}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            return (
              <li
                key={key + option.value}
                {...optionProps}
                className="py-2 px-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
              >
                <p className="text-base font-semibold">
                  {option.label} <span className="font-base">({option.role})</span>
                </p>
                <p className="text-sm text-gray-600">{option.companyName}</p>
              </li>
            );
          }}
          onChange={(e) => onParentUserChange(e.target.value)}
        />
      )}
    </>
  );
};

export default UserAssignRoleSelector;
